// [S101] Telemedicina Fase 1 — SALA do MÉDICO (WebRTC 1:1).
// O médico é o OFFERER: ao detectar o paciente na sala (tele:joined peers>0 ou
// tele:peer-joined), cria a oferta. Signaling pelo socket AUTENTICADO existente
// (namespace /companyId). ATENÇÃO: o socket é COMPARTILHADO (SocketWorker
// singleton) → removemos só os NOSSOS listeners no unmount; NUNCA desconectamos.
// A mídia é P2P (DTLS-SRTP); não gravamos. Gated por telemedicina_enabled (backend).
import React, { useState, useEffect, useRef, useContext } from "react";
import { useParams, useHistory } from "react-router-dom";
import { makeStyles } from "@material-ui/core/styles";
import { Box, IconButton, Typography, CircularProgress, Button } from "@material-ui/core";
import MicIcon from "@material-ui/icons/Mic";
import MicOffIcon from "@material-ui/icons/MicOff";
import VideocamIcon from "@material-ui/icons/Videocam";
import VideocamOffIcon from "@material-ui/icons/VideocamOff";
import CallEndIcon from "@material-ui/icons/CallEnd";
import { toast } from "react-toastify";

import { AuthContext } from "../../context/Auth/AuthContext";
import { socketConnection } from "../../services/socket";
import {
  getTeleconsulta,
  getTeleconsultaRtc,
  startTeleconsulta,
  endTeleconsulta
} from "../../services/teleconsultaService";

const useStyles = makeStyles(() => ({
  room: { position: "fixed", inset: 0, background: "#05070d", zIndex: 1300, overflow: "hidden" },
  remote: { position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", background: "#05070d" },
  overlay: {
    position: "absolute", inset: 0, display: "flex", flexDirection: "column",
    alignItems: "center", justifyContent: "center", gap: 14, color: "#9aa7c2"
  },
  local: {
    position: "absolute", right: 16, bottom: 108, width: 132, height: 184, objectFit: "cover",
    borderRadius: 12, border: "2px solid rgba(255,255,255,.2)", background: "#05070d",
    transform: "scaleX(-1)", zIndex: 3
  },
  topbar: {
    position: "absolute", top: 0, left: 0, right: 0, zIndex: 4, padding: "16px 20px",
    display: "flex", flexDirection: "column",
    background: "linear-gradient(180deg, rgba(0,0,0,.55), rgba(0,0,0,0))"
  },
  who: { color: "#fff", fontWeight: 600, fontSize: 16 },
  status: { color: "#cbd5e1", fontSize: 13 },
  controls: {
    position: "absolute", left: 0, right: 0, bottom: 0, zIndex: 4, padding: "22px 16px",
    display: "flex", alignItems: "center", justifyContent: "center", gap: 22,
    background: "linear-gradient(0deg, rgba(0,0,0,.6), rgba(0,0,0,0))"
  },
  cbtn: { width: 60, height: 60, background: "rgba(255,255,255,.16)", color: "#fff", "&:hover": { background: "rgba(255,255,255,.26)" } },
  hang: { background: "#ef4444", "&:hover": { background: "#dc2626" } },
  errorWrap: { position: "fixed", inset: 0, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 16, zIndex: 1300, background: "#0b1220", color: "#eef2fb" }
}));

const TeleconsultaRoom = () => {
  const classes = useStyles();
  const { sessionId } = useParams();
  const history = useHistory();
  const { user } = useContext(AuthContext);

  const localRef = useRef(null);
  const remoteRef = useRef(null);
  const pcRef = useRef(null);
  const socketRef = useRef(null);
  const localStreamRef = useRef(null);
  const offeringRef = useRef(false);
  const startedRef = useRef(false);
  const endedRef = useRef(false);

  const [patientName, setPatientName] = useState("");
  const [status, setStatus] = useState("Iniciando…");
  const [connected, setConnected] = useState(false);
  const [micOn, setMicOn] = useState(true);
  const [camOn, setCamOn] = useState(true);
  const [error, setError] = useState("");

  const sid = Number(sessionId);

  // médico é o OFFERER — cria a oferta quando detecta o paciente.
  const makeOffer = async () => {
    const pc = pcRef.current;
    if (!pc || offeringRef.current || pc.signalingState !== "stable") return;
    try {
      offeringRef.current = true;
      const offer = await pc.createOffer();
      await pc.setLocalDescription(offer);
      socketRef.current && socketRef.current.emit("tele:offer", { sessionId: sid, sdp: pc.localDescription });
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("[tele] makeOffer", e);
    } finally {
      offeringRef.current = false;
    }
  };

  const doEnd = (msg) => {
    if (endedRef.current) return;
    endedRef.current = true;
    try { socketRef.current && socketRef.current.emit("tele:end", { sessionId: sid }); } catch (e) {}
    endTeleconsulta(sid).catch(() => {});
    try { pcRef.current && pcRef.current.close(); } catch (e) {}
    try { localStreamRef.current && localStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) {}
    if (msg) toast.info(msg);
    history.push("/teleconsultas");
  };

  useEffect(() => {
    let mounted = true;
    let socket = null;
    const h = {};

    async function setup() {
      try {
        const { session } = await getTeleconsulta(sid);
        if (!mounted) return;
        setPatientName(session.patientName || "Paciente");
        if (session.status === "ended" || session.status === "cancelled") {
          setError("Esta teleconsulta já foi encerrada.");
          return;
        }

        const rtc = await getTeleconsultaRtc(sid);
        const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "user" }, audio: true });
        if (!mounted) { stream.getTracks().forEach((t) => t.stop()); return; }
        localStreamRef.current = stream;
        if (localRef.current) { localRef.current.srcObject = stream; localRef.current.muted = true; }

        const pc = new RTCPeerConnection({ iceServers: (rtc && rtc.iceServers) || [] });
        pcRef.current = pc;
        stream.getTracks().forEach((t) => pc.addTrack(t, stream));
        pc.onicecandidate = (ev) => {
          if (ev.candidate && socketRef.current) socketRef.current.emit("tele:ice-candidate", { sessionId: sid, candidate: ev.candidate });
        };
        pc.ontrack = (ev) => {
          if (remoteRef.current && remoteRef.current.srcObject !== ev.streams[0]) remoteRef.current.srcObject = ev.streams[0];
        };
        pc.onconnectionstatechange = () => {
          const st = pc.connectionState;
          if (st === "connected") {
            setConnected(true); setStatus("Conectado");
            if (!startedRef.current) { startedRef.current = true; startTeleconsulta(sid).catch(() => {}); }
          } else if (st === "connecting") setStatus("Conectando…");
          else if (st === "disconnected") setStatus("Reconectando…");
          else if (st === "failed") setStatus("Falha na conexão");
        };

        socket = socketConnection({ user: { companyId: user.companyId, id: user.id } });
        socketRef.current = socket;
        h.joined = (p) => { if (p && p.peers > 0) makeOffer(); };
        h.peerJoined = () => { setStatus("Paciente na sala…"); makeOffer(); };
        h.answer = (p) => { if (p && p.sdp && pcRef.current) pcRef.current.setRemoteDescription(new RTCSessionDescription(p.sdp)).catch(() => {}); };
        h.ice = (p) => { if (p && p.candidate && pcRef.current) pcRef.current.addIceCandidate(new RTCIceCandidate(p.candidate)).catch(() => {}); };
        h.peerLeft = () => setStatus("O paciente saiu");
        h.ended = () => doEnd("O paciente encerrou a consulta.");
        h.connect = () => socket.emit("tele:join", { sessionId: sid });
        socket.on("tele:joined", h.joined);
        socket.on("tele:peer-joined", h.peerJoined);
        socket.on("tele:answer", h.answer);
        socket.on("tele:ice-candidate", h.ice);
        socket.on("tele:peer-left", h.peerLeft);
        socket.on("tele:ended", h.ended);
        socket.on("connect", h.connect);
        // O wrapper (SocketWorker) é SINGLETON e já está conectado (o app usa o
        // socket o tempo todo) e NÃO expõe .connected → emitimos o join JÁ (o
        // socket.io-client interno bufferiza se estiver a meio de conectar) e
        // reemitimos em "connect" pra cobrir reconexão. Join duplicado é inócuo.
        socket.emit("tele:join", { sessionId: sid });
        setStatus("Aguardando o paciente…");
      } catch (e) {
        if (!mounted) return;
        if (e && e.name === "NotAllowedError") setError("Permita o acesso à câmera e ao microfone para iniciar.");
        else if (e && e.name === "NotFoundError") setError("Câmera/microfone não encontrados.");
        else setError("Não foi possível iniciar a teleconsulta.");
      }
    }
    setup();

    return () => {
      mounted = false;
      if (socket) {
        // remove SÓ os nossos listeners — o socket é COMPARTILHADO, nunca desconectar.
        socket.off("tele:joined", h.joined);
        socket.off("tele:peer-joined", h.peerJoined);
        socket.off("tele:answer", h.answer);
        socket.off("tele:ice-candidate", h.ice);
        socket.off("tele:peer-left", h.peerLeft);
        socket.off("tele:ended", h.ended);
        socket.off("connect", h.connect);
      }
      try { pcRef.current && pcRef.current.close(); } catch (e) {}
      try { localStreamRef.current && localStreamRef.current.getTracks().forEach((t) => t.stop()); } catch (e) {}
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleMic = () => {
    const on = !micOn; setMicOn(on);
    localStreamRef.current && localStreamRef.current.getAudioTracks().forEach((t) => { t.enabled = on; });
  };
  const toggleCam = () => {
    const on = !camOn; setCamOn(on);
    localStreamRef.current && localStreamRef.current.getVideoTracks().forEach((t) => { t.enabled = on; });
  };

  if (error) {
    return (
      <Box className={classes.errorWrap}>
        <Typography variant="h6">{error}</Typography>
        <Button variant="outlined" style={{ color: "#fff", borderColor: "#334155" }} onClick={() => history.push("/teleconsultas")}>Voltar</Button>
      </Box>
    );
  }

  return (
    <Box className={classes.room}>
      <video ref={remoteRef} autoPlay playsInline className={classes.remote} />
      {!connected && (
        <Box className={classes.overlay}>
          <CircularProgress style={{ color: "#3b82f6" }} />
          <Typography>{status}</Typography>
        </Box>
      )}
      <video ref={localRef} autoPlay playsInline muted className={classes.local} />
      <Box className={classes.topbar}>
        <Typography className={classes.who}>{patientName}</Typography>
        <Typography className={classes.status}>{status}</Typography>
      </Box>
      <Box className={classes.controls}>
        <IconButton className={classes.cbtn} onClick={toggleMic} title="Microfone">{micOn ? <MicIcon /> : <MicOffIcon />}</IconButton>
        <IconButton className={`${classes.cbtn} ${classes.hang}`} onClick={() => doEnd()} title="Encerrar"><CallEndIcon /></IconButton>
        <IconButton className={classes.cbtn} onClick={toggleCam} title="Câmera">{camOn ? <VideocamIcon /> : <VideocamOffIcon />}</IconButton>
      </Box>
    </Box>
  );
};

export default TeleconsultaRoom;
